import React, { useEffect, useMemo, useState } from 'react';
import {
  columnsObj,
  defaultColumnKeys,
} from '../../../../platform/ux/Home/components/PacingTab/utils';
import { PacingDataObjectOrderByInput } from '../../../GraphQl/Queries/GET_PACING_DATA_CONNECTION';
import { determineUserAccess } from '../../../permissions/userLevelAccess';
import { TABLE_ORDER_ASC, TABLE_ORDER_DESC } from '../../../utils/constants/constants';
import { hasSpecialCharacters, formatCampaignName } from '../../../utils/campaignNaming';
import { formatCurrency2SigFig, formatWholeNumberNaN } from '../../../utils/campaigns';

function toTableData(edges) {
  return edges.map((i) => {
    const p = i.node;
    const campaignOrder = i.node.campaignOrder;
    const campaignOrderOverallData = campaignOrder.aggregateData.overallData;

    // Apply naming convention to campaign name if it doesn't have special characters
    const campaignName =
      campaignOrder.name && !hasSpecialCharacters(campaignOrder.name)
        ? formatCampaignName(campaignOrder)
        : campaignOrder.name;

    if (campaignOrder.pricingModel === 'Percentage') {
      return {
        key: i.cursor,
        id: campaignOrder.id,
        actualImpressions: formatCurrency2SigFig(p?.budgetSpent || 0),
        daysIntoFlight: p.daysIntoFlight,
        impressionsLeft: formatCurrency2SigFig(p?.budgetLeft || 0), // Total Budget = Spent
        pace: campaignOrder.cpm > 0 ? p.pace : null,
        paceYesterday: campaignOrder.cpm > 0 ? p.paceYesterday : null,
        paceTwoDaysAgo: campaignOrder.cpm > 0 ? p.paceTwoDaysAgo : null,
        paceThreeDaysAgo: campaignOrder.cpm > 0 ? p.paceThreeDaysAgo : null,
        paceFourDaysAgo: campaignOrder.cpm > 0 ? p.paceFourDaysAgo : null,
        paceFiveDaysAgo: campaignOrder.cpm > 0 ? p.paceFiveDaysAgo : null,
        paceSixDaysAgo: campaignOrder.cpm > 0 ? p.paceSixDaysAgo : null,
        paceSevenDaysAgo: campaignOrder.cpm > 0 ? p.paceSevenDaysAgo : null,
        dailyAvgImpressions: formatCurrency2SigFig(p?.dailyAvgBudget || 0),
        dailyAvgImpressionsLeft: formatCurrency2SigFig(p?.dailyAvgBudgetLeft || 0),
        daysLeft: p.daysLeft,
        daysLeftFromSchedule: p.daysLeftFromSchedule,
        name: campaignName,
        cpm: campaignOrder.cpm,
        dsp: campaignOrder.demandSidePlatform,
        budget: campaignOrder.budget,
        impressionsYesterday: formatCurrency2SigFig(p?.budgetYesterday || 0),
        expectedImpressions: formatCurrency2SigFig(p?.totalBudget || 0),
        roas: p.roas,
        ctr: p.ctr,
        status: campaignOrder.status,
        endDate: campaignOrder.endDate,
        attributionWindow: campaignOrder.attributionWindow,
      };
    }
    return {
      key: i.cursor,
      id: campaignOrder.id,
      actualImpressions: formatWholeNumberNaN(campaignOrderOverallData?.aggImpressions || 0),
      daysIntoFlight: p.daysIntoFlight,
      impressionsLeft: formatWholeNumberNaN(p?.impressionsLeft || 0),
      pace: campaignOrder.cpm > 0 ? p.pace : null,
      paceYesterday: campaignOrder.cpm > 0 ? p.paceYesterday : null,
      paceTwoDaysAgo: campaignOrder.cpm > 0 ? p.paceTwoDaysAgo : null,
      paceThreeDaysAgo: campaignOrder.cpm > 0 ? p.paceThreeDaysAgo : null,
      paceFourDaysAgo: campaignOrder.cpm > 0 ? p.paceFourDaysAgo : null,
      paceFiveDaysAgo: campaignOrder.cpm > 0 ? p.paceFiveDaysAgo : null,
      paceSixDaysAgo: campaignOrder.cpm > 0 ? p.paceSixDaysAgo : null,
      paceSevenDaysAgo: campaignOrder.cpm > 0 ? p.paceSevenDaysAgo : null,
      dailyAvgImpressions: formatWholeNumberNaN(p?.dailyAvgImpressions || 0),
      dailyAvgImpressionsLeft: formatWholeNumberNaN(p?.dailyAvgImpressionsLeft || 0),
      daysLeft: p.daysLeft,
      daysLeftFromSchedule: p.daysLeftFromSchedule,
      name: campaignName,
      cpm: campaignOrder.cpm,
      dsp: campaignOrder.demandSidePlatform,
      budget: campaignOrder.budget,
      impressionsYesterday: formatWholeNumberNaN(p?.impressionsYesterday || 0),
      expectedImpressions: formatWholeNumberNaN(p?.expectedImpressions || 0),
      roas: p.roas,
      ctr: p.ctr,
      status: campaignOrder.status,
      endDate: campaignOrder.endDate,
      attributionWindow: campaignOrder.attributionWindow,
    };
  });
}

const PacingController = (props) => {
  //TODO transform data to have the computation

  const { skip, setSkip, first, setFirst, getExport, loadingExport, exportData, setOrderBy } =
    props;

  let tableData = [];
  const [selectedCIDs, setSelectedCIDs] = useState([]);
  const [selectedDSP, setSelectedDSP] = useState();
  const [activeColumns, setActiveColumns] = useState(defaultColumnKeys);
  const { data, isLoading, filterOptions, refetch } = props;
  const [pagination, setPagination] = useState({
    current: skip / first < 1 ? 1 : skip / first,
    pageSize: first,
  });

  const userPermission = determineUserAccess(props.currentUser.permission);

  if (data && data.pacingDataObjectsConnection.edges) {
    const edges = data.pacingDataObjectsConnection.edges;
    tableData = toTableData(edges);
  }

  const total = useMemo(() => {
    return data && data.total ? data.total.aggregate.count : 0;
  }, [data]);

  const campaignDataOptions = useMemo(() => {
    if (
      filterOptions &&
      filterOptions.campaignOrdersConnection &&
      filterOptions.campaignOrdersConnection.edges
    ) {
      const edges = filterOptions.campaignOrdersConnection.edges;
      return edges.map((e) => {
        // Apply naming convention to campaign name if it doesn't have special characters
        const campaignName =
          e.node.name && !hasSpecialCharacters(e.node.name)
            ? formatCampaignName(e.node)
            : e.node.name;

        return {
          id: e.node.id,
          name: campaignName,
          endDate: e.node.endDate,
        };
      });
    }
    return [];
  }, [filterOptions]);

  const dspDataOptions = useMemo(() => {
    if (filterOptions && filterOptions.getPacingReportFilterOptions) {
      return filterOptions.getPacingReportFilterOptions.dsps;
    }
    return [];
  }, [filterOptions]);

  const onExport = () => {
    getExport();
  };

  const exportTableData = useMemo(() => {
    if (exportData && exportData.pacingDataObjectsConnection) {
      return toTableData(exportData.pacingDataObjectsConnection.edges);
    }
  }, [exportData]);

  const handleSorterChange = (key, order) => {
    if (!order) {
      // default
      setOrderBy(PacingDataObjectOrderByInput.daysLeft_DESC);
    }

    if (key === columnsObj.name.key) {
      if (order === TABLE_ORDER_ASC) {
        setOrderBy(PacingDataObjectOrderByInput.campaignName_ASC);
      } else if (order === TABLE_ORDER_DESC) {
        setOrderBy(PacingDataObjectOrderByInput.campaignName_DESC);
      }
    }

    if (key === columnsObj.daysLeft.key) {
      if (order === TABLE_ORDER_ASC) {
        setOrderBy(PacingDataObjectOrderByInput.daysLeft_ASC);
      } else if (order === TABLE_ORDER_DESC) {
        setOrderBy(PacingDataObjectOrderByInput.daysLeft_DESC);
      }
    }
    if (key === columnsObj.daysLeftFromSchedule.key) {
      if (order === TABLE_ORDER_ASC) {
        setOrderBy(PacingDataObjectOrderByInput.daysLeftFromSchedule_ASC);
      } else if (order === TABLE_ORDER_DESC) {
        setOrderBy(PacingDataObjectOrderByInput.daysLeftFromSchedule_DESC);
      }
    }
    if (key === columnsObj.pace.key) {
      if (order === TABLE_ORDER_ASC) {
        setOrderBy(PacingDataObjectOrderByInput.pace_ASC);
      } else if (order === TABLE_ORDER_DESC) {
        setOrderBy(PacingDataObjectOrderByInput.pace_DESC);
      }
    }
  };

  useEffect(() => {
    setFirst(pagination.pageSize);
    setSkip(pagination.current * pagination.pageSize - pagination.pageSize);
  }, [pagination, setSkip, setFirst]);

  useEffect(() => {
    refetch({
      first: pagination.pageSize,
      skip: pagination.current * pagination.pageSize - pagination.pageSize,
      campaignOrderIds: selectedCIDs.length > 0 ? selectedCIDs : undefined,
    });
  }, [pagination, refetch, selectedCIDs]);

  return React.cloneElement(props.children, {
    tableData,
    isLoading,
    currentPagination: pagination,
    total,
    setPagination,
    onExport,
    dspDataOptions,
    campaignDataOptions,
    selectedCIDs,
    setSelectedCIDs,
    selectedDSP,
    setSelectedDSP,
    activeColumns,
    loadingExport,
    exportTableData,
    setActiveColumns,
    userPermission,
    handleSorterChange,
    refetch,
  });
};

export default PacingController;
